import { useCallback } from 'react';
import { useReactiveVar } from '@apollo/client';

import {
  paletteState,
  defaultPaletteState,
  PaletteEntity,
  PaletteMode,
} from 'core/state';

export interface IUsePalette {
  open: boolean;
  query: string;
  mode: PaletteMode;
  activeEntity: PaletteEntity | null;
  selectedIndex: number;
  openPalette: () => void;
  closePalette: () => void;
  toggle: () => void;
  /** Open the read-only shortcuts cheat sheet (`?`), reusing the same overlay. */
  openHelp: () => void;
  setQuery: (query: string) => void;
  enterEntityActions: (entity: PaletteEntity) => void;
  back: () => void;
  setSelectedIndex: (index: number) => void;
}

/**
 * Reads `paletteState` and exposes its mutations. Follows the `useSearch`
 * idiom — spread the current var and reassign — and memoizes every op so the
 * container's keyboard/hotkey effects keep stable dependencies.
 */
export const usePalette = (): IUsePalette => {
  const state = useReactiveVar(paletteState);

  const openPalette = useCallback(() => {
    paletteState({ ...defaultPaletteState, open: true });
  }, []);

  const closePalette = useCallback(() => {
    paletteState({ ...defaultPaletteState });
  }, []);

  const toggle = useCallback(() => {
    if (paletteState().open) {
      paletteState({ ...defaultPaletteState });
    } else {
      paletteState({ ...defaultPaletteState, open: true });
    }
  }, []);

  const openHelp = useCallback(() => {
    paletteState({ ...defaultPaletteState, open: true, mode: 'help' });
  }, []);

  const setQuery = useCallback((query: string) => {
    paletteState({
      ...paletteState(),
      query,
      mode: 'search',
      activeEntity: null,
      selectedIndex: 0,
    });
  }, []);

  const enterEntityActions = useCallback((entity: PaletteEntity) => {
    paletteState({
      ...paletteState(),
      mode: 'entityActions',
      activeEntity: entity,
      selectedIndex: 0,
    });
  }, []);

  const back = useCallback(() => {
    paletteState({
      ...paletteState(),
      mode: 'search',
      activeEntity: null,
      selectedIndex: 0,
    });
  }, []);

  const setSelectedIndex = useCallback((index: number) => {
    paletteState({ ...paletteState(), selectedIndex: index });
  }, []);

  return {
    open: state.open,
    query: state.query,
    mode: state.mode,
    activeEntity: state.activeEntity,
    selectedIndex: state.selectedIndex,
    openPalette,
    closePalette,
    toggle,
    openHelp,
    setQuery,
    enterEntityActions,
    back,
    setSelectedIndex,
  };
};
